/**
 * OpportunityCalculatorV3 — v2.3 (2026-05-07).
 *
 * Branching question structure: 40 main questions across 10 checkpoints.
 * Conditional follow-up questions fire after each main answer based on
 * point value. Follow-ups are visually nested 32px with a "Follow-up" tag.
 *
 * Key differences from v2.2:
 *   - Imports from calculator-questions-v2_3 (not calculator-questions)
 *   - Progress: "Question 8 of 40 main · plus 3 follow-ups so far"
 *   - Branching: follow-ups inserted after parent, fired by point value
 *   - Skip on parent: NO follow-ups fire
 *   - Quick Score: 7 questions, no follow-ups
 *   - Confidence: based on MAIN question completion rate
 *   - Per-section breakdown: main answered + follow-ups answered + score
 *
 * Manufacturing-only. M-PACT with hyphen. No emojis. No exclamation points.
 * Roles: bdr/ae/manager/executive/marketing/se. Never "rep".
 */

import { useMemo, useState, useCallback } from "react";
import {
  MAIN_QUESTIONS,
  QUICK_QUESTIONS,
  GRADED_OPTIONS,
  FOLLOW_UP_RULES,
  computeScore,
  sectionFor,
  SECTION_STRENGTH_LINES,
  SECTION_RISK_LINES,
  questionById,
  getFollowUps,
  type Answers,
  type CalculatorResult,
  type CalcQuestion,
  type SectionId,
} from "@shared/calculator-questions-v2_3";
import { getHealthColor, getForecastCategory } from "@shared/schema";

const BLACK = "#0B0B0F";
const RED = "#DC2626";
const BG = "#FAFAF7";
const SLATE_BORDER = "#E5E5E7";
const SLATE_TEXT = "#525258";
const SLATE_MUTED = "#8E8E94";
const FOLLOW_UP_INDENT = 32;
const FOLLOW_UP_BG = "#FAFAF7";

type Mode = "FULL" | "QUICK";

interface Props {
  accountName?: string | null;
  onSaveAccount?: (result: CalculatorResult, mode: Mode) => void;
  inAip?: boolean;
  /** When true, suppress the mode-select and force Quick Score only (marketing) */
  quickOnly?: boolean;
}

const STORAGE_KEY = "aip.calculator.runs.v2_3";

interface SavedRun {
  ts: number;
  accountName: string | null;
  mode: Mode;
  answers: Answers;
  composite: number;
  band: CalculatorResult["band"];
}

function persistRun(run: SavedRun) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const list: SavedRun[] = raw ? JSON.parse(raw) : [];
    list.unshift(run);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list.slice(0, 50)));
  } catch { /* localStorage unavailable — ignore */ }
}

/**
 * Build the live question queue for FULL mode.
 * Starts as the 40 main questions. As answers come in, follow-ups are
 * inserted immediately after their parent.
 */
function buildQueue(answers: Answers): CalcQuestion[] {
  const queue: CalcQuestion[] = [...MAIN_QUESTIONS];
  // Walk in order; insert follow-ups after each answered main question
  let i = 0;
  while (i < queue.length) {
    const q = queue[i];
    const ans = answers[q.id];
    if (ans !== undefined && ans !== null && !q.isFollowUp) {
      // Get follow-ups that should fire for this answer
      const followUpIds = getFollowUps(q.id, ans);
      if (followUpIds.length > 0) {
        // Find which ones are not already in the queue
        const alreadyInQueue = new Set(queue.map((x) => x.id));
        const toInsert: CalcQuestion[] = [];
        for (const id of followUpIds) {
          if (!alreadyInQueue.has(id)) {
            const fq = questionById(id);
            if (fq) toInsert.push(fq);
          }
        }
        if (toInsert.length > 0) {
          // Insert after position i
          queue.splice(i + 1, 0, ...toInsert);
        }
      }
    }
    i++;
  }
  return queue;
}

export default function OpportunityCalculatorV3({
  accountName = null,
  onSaveAccount,
  inAip = false,
  quickOnly = false,
}: Props) {
  const [mode, setMode] = useState<Mode | null>(quickOnly ? "QUICK" : null);
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [submitted, setSubmitted] = useState(false);
  const [savedAccountName, setSavedAccountName] = useState<string | null>(null);

  // The live question queue — rebuilt whenever answers change (FULL mode)
  const activeQueue: CalcQuestion[] = useMemo(() => {
    if (mode === "QUICK") return QUICK_QUESTIONS;
    if (mode === "FULL") return buildQueue(answers);
    return [];
  }, [mode, answers]);

  const totalCount = activeQueue.length;
  const currentQ = activeQueue[stepIndex] ?? null;

  // Main question count (for progress display)
  const mainQsInQueue = useMemo(
    () => activeQueue.filter((q) => !q.isFollowUp),
    [activeQueue],
  );
  const mainAnsweredSoFar = useMemo(
    () => mainQsInQueue.filter((q) => answers[q.id] !== undefined && answers[q.id] !== null).length,
    [mainQsInQueue, answers],
  );

  // Follow-ups answered so far
  const followUpsAnsweredSoFar = useMemo(
    () => activeQueue.filter((q) => q.isFollowUp && answers[q.id] !== undefined && answers[q.id] !== null).length,
    [activeQueue, answers],
  );

  // Current question position among main questions (for progress counter)
  const mainIndexUpToNow = useMemo(() => {
    if (!currentQ) return mainQsInQueue.length;
    // Count main questions that appear at or before the current step
    let count = 0;
    for (let i = 0; i <= stepIndex && i < activeQueue.length; i++) {
      if (!activeQueue[i].isFollowUp) count++;
    }
    return count;
  }, [currentQ, stepIndex, activeQueue, mainQsInQueue.length]);

  const sectionsInRun = useMemo(() => {
    const seen = new Set<SectionId>();
    const out: SectionId[] = [];
    for (const q of activeQueue) {
      if (!seen.has(q.section)) { seen.add(q.section); out.push(q.section); }
    }
    return out;
  }, [activeQueue]);

  const currentSectionIdx = currentQ ? sectionsInRun.indexOf(currentQ.section) : -1;

  // Score computed over all questions that have been presented (main + fired follow-ups)
  const presentedQuestions = useMemo(
    () => activeQueue.filter((q) => answers[q.id] !== undefined),
    [activeQueue, answers],
  );

  const result = useMemo(
    () => computeScore(answers, activeQueue, MAIN_QUESTIONS),
    [answers, activeQueue],
  );

  function pickMode(m: Mode) {
    setMode(m);
    setStepIndex(0);
    setAnswers({});
    setSubmitted(false);
    setSavedAccountName(null);
  }

  function pick(qid: string, value: number) {
    const newAnswers = { ...answers, [qid]: value };
    setAnswers(newAnswers);
    window.setTimeout(() => {
      // Rebuild queue with new answer to get follow-ups inserted
      const newQueue = mode === "QUICK" ? QUICK_QUESTIONS : buildQueue(newAnswers);
      const nextIndex = stepIndex + 1;
      if (nextIndex >= newQueue.length) {
        setSubmitted(true);
      } else {
        setStepIndex(nextIndex);
      }
    }, 220);
  }

  function skip(qid: string) {
    // Skipped questions: set null, NO follow-ups fire
    setAnswers((prev) => ({ ...prev, [qid]: null }));
    window.setTimeout(() => {
      setStepIndex((s) => {
        const newQueue = mode === "QUICK" ? QUICK_QUESTIONS : buildQueue({ ...answers, [qid]: null });
        if (s + 1 >= newQueue.length) { setSubmitted(true); return s; }
        return s + 1;
      });
    }, 100);
  }

  function back() {
    setStepIndex((s) => Math.max(0, s - 1));
    setSubmitted(false);
  }

  function reset() {
    if (quickOnly) {
      setMode("QUICK");
    } else {
      setMode(null);
    }
    setStepIndex(0);
    setAnswers({});
    setSubmitted(false);
    setSavedAccountName(null);
  }

  function handleSaveAccount() {
    if (!onSaveAccount || !mode) return;
    onSaveAccount(result, mode);
    persistRun({
      ts: Date.now(),
      accountName: accountName ?? null,
      mode,
      answers,
      composite: result.composite,
      band: result.band,
    });
    setSavedAccountName(accountName ?? "account");
  }

  function handleEmail() {
    if (typeof window === "undefined") return;
    const subject = encodeURIComponent(
      `Opportunity checkpoint audit${accountName ? ` — ${accountName}` : ""}: ${result.composite}/100`,
    );
    const lines = [
      `Composite: ${result.composite}/100 (${result.band})`,
      `Verdict: ${result.verdictLine}`,
      `Confidence: ${result.mainAnsweredCount} of ${result.mainTotalCount} main answered (${result.confidenceTag})`,
      `Follow-ups answered: ${result.followUpAnsweredCount}`,
      "",
      "Per-section:",
      ...result.sections.map(
        (s) =>
          `  ${s.title}: ${s.pct !== null ? `${s.pct}/100` : "—"} (${
            s.answered - s.followUpsAnswered
          } main + ${s.followUpsAnswered} follow-ups answered)`,
      ),
    ];
    const body = encodeURIComponent(lines.join("\n"));
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  }

  // ── INTRO ──────────────────────────────────────────────────────────────────
  if (mode === null) {
    return (
      <div
        style={{
          background: "#FFFFFF",
          border: `1px solid ${SLATE_BORDER}`,
          borderRadius: 16,
          overflow: "hidden",
          boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 12px 32px -16px rgba(15,23,42,0.12)",
        }}
        data-testid="calculator-intro"
      >
        <div style={{ padding: "28px 28px 8px" }}>
          <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.28em", color: RED, textTransform: "uppercase", marginBottom: 10 }}>
            Checkpoint calculator
          </p>
          <h2 style={{ fontSize: 28, fontWeight: 800, color: BLACK, letterSpacing: "-0.02em", lineHeight: 1.1, margin: 0, marginBottom: 8 }}>
            Opportunity Calculator
          </h2>
          <p style={{ fontSize: 14.5, color: SLATE_TEXT, lineHeight: 1.55, maxWidth: 720, margin: 0 }}>
            Score a deal against the checkpoint questions that determine whether it is worth pursuing or you should cut bait now.
            {accountName ? (
              <> Scoring against <strong style={{ color: BLACK }}>{accountName}</strong>.</>
            ) : null}
          </p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, background: SLATE_BORDER, padding: "20px 1px 28px", marginTop: 16 }}>
          <button
            type="button"
            onClick={() => pickMode("FULL")}
            data-testid="mode-full"
            style={{ textAlign: "left", padding: "20px 24px", background: "#FFFFFF", border: "none", cursor: "pointer", transition: "background 0.15s" }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = BG; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "#FFFFFF"; }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.18em", color: BLACK, textTransform: "uppercase" }}>Full checkpoint audit</span>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", color: SLATE_TEXT, textTransform: "uppercase" }}>40 main q · ~8 min</span>
            </div>
            <div style={{ fontSize: 19, fontWeight: 700, color: BLACK, lineHeight: 1.2, marginBottom: 8 }}>
              40 checkpoint questions across ten checkpoints
            </div>
            <p style={{ fontSize: 13, color: SLATE_TEXT, lineHeight: 1.55, margin: 0 }}>
              Budget, Authority, Need, Timeline, Compliance, Financial, Competitor, Organization, People Network, and Risk. Branching follow-up questions fire when an answer warrants drilling deeper. Returns a composite score, per-section breakdown, and a written go/no-go verdict.
            </p>
          </button>

          <button
            type="button"
            onClick={() => pickMode("QUICK")}
            data-testid="mode-quick"
            style={{ textAlign: "left", padding: "20px 24px", background: "#FFFFFF", border: "none", cursor: "pointer", transition: "background 0.15s" }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = BG; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = "#FFFFFF"; }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.18em", color: RED, textTransform: "uppercase" }}>Quick Score</span>
              <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", color: SLATE_TEXT, textTransform: "uppercase" }}>7 q · 90 sec</span>
            </div>
            <div style={{ fontSize: 19, fontWeight: 700, color: BLACK, lineHeight: 1.2, marginBottom: 8 }}>
              Directional read in ninety seconds
            </div>
            <p style={{ fontSize: 13, color: SLATE_TEXT, lineHeight: 1.55, margin: 0 }}>
              One checkpoint question each from Budget, Authority, Need, Timeline, Competitor, People Network, and Risk. Same scoring math. No follow-up questions. For sizing up a deal in the moment.
            </p>
          </button>
        </div>

        <div style={{ padding: "18px 28px 28px", borderTop: `1px solid ${SLATE_BORDER}` }}>
          <p style={{ fontSize: 12, color: SLATE_TEXT, margin: 0, lineHeight: 1.55 }}>
            40 main checkpoint questions across 10 checkpoints. Conditional follow-up questions drill deeper when an answer is weak, partial, or suspicious. Five-point graded scale — skip any question you cannot answer yet. Manufacturing-only: industrial, automotive, aerospace, building materials, industrial equipment, contract manufacturing.
          </p>
        </div>
      </div>
    );
  }

  // ── RESULT ─────────────────────────────────────────────────────────────────
  if (submitted || stepIndex >= totalCount) {
    // Canon §5 5-band palette driven by composite score, not the 3-band
    // GREEN/AMBER/RED legacy on result.band.
    const bandColor = getHealthColor(result.composite);
    const bandBg   = `${bandColor}1A`; // ~10% alpha tint
    const bandLabel = getForecastCategory(result.composite);

    return (
      <div
        style={{
          background: "#FFFFFF",
          border: `1px solid ${SLATE_BORDER}`,
          borderRadius: 16,
          overflow: "hidden",
          boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 12px 32px -16px rgba(15,23,42,0.12)",
        }}
        data-testid="calculator-result"
      >
        {/* Composite header */}
        <div style={{ padding: "28px", borderBottom: `1px solid ${SLATE_BORDER}` }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 24, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 260 }}>
              <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.28em", color: RED, textTransform: "uppercase", margin: 0, marginBottom: 8 }}>
                {mode === "QUICK" ? "Quick Score" : "Full checkpoint audit"}{accountName ? ` · ${accountName}` : ""}
              </p>
              <h3 style={{ fontSize: 26, fontWeight: 800, color: bandColor, letterSpacing: "-0.02em", lineHeight: 1.1, margin: 0, marginBottom: 6 }}>
                {result.verdictLine}
              </h3>
              <p style={{ fontSize: 13, color: SLATE_TEXT, lineHeight: 1.55, margin: 0 }}>
                {result.mainAnsweredCount} of {result.mainTotalCount} main questions answered
                {mode === "FULL" && result.followUpAnsweredCount > 0 && (
                  <> · {result.followUpAnsweredCount} follow-up{result.followUpAnsweredCount !== 1 ? "s" : ""}</>
                )}
                {" · "}<span style={{ fontWeight: 700 }}>{result.confidenceTag} confidence</span>
              </p>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 12, padding: "12px 18px", background: bandBg, borderRadius: 12, border: `2px solid ${bandColor}` }}>
                <div style={{ textAlign: "left" }}>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.18em", color: bandColor, textTransform: "uppercase" }}>{bandLabel}</div>
                  <div style={{ fontSize: 36, fontWeight: 800, color: BLACK, lineHeight: 1, marginTop: 2 }}>
                    {result.composite}<span style={{ fontSize: 14, color: SLATE_TEXT, fontWeight: 600 }}> / 100</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Per-section breakdown */}
        <div style={{ padding: "24px 28px", borderBottom: `1px solid ${SLATE_BORDER}` }}>
          <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.18em", color: BLACK, textTransform: "uppercase", margin: 0, marginBottom: 14 }}>
            Per-section breakdown
          </p>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", color: SLATE_MUTED, fontWeight: 700, fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", paddingBottom: 8, paddingRight: 16 }}>Section</th>
                  {mode === "FULL" && (
                    <th style={{ textAlign: "right", color: SLATE_MUTED, fontWeight: 700, fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", paddingBottom: 8, paddingRight: 16 }}>Main</th>
                  )}
                  {mode === "FULL" && (
                    <th style={{ textAlign: "right", color: SLATE_MUTED, fontWeight: 700, fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", paddingBottom: 8, paddingRight: 16 }}>Follow-ups</th>
                  )}
                  {mode !== "FULL" && (
                    <th style={{ textAlign: "right", color: SLATE_MUTED, fontWeight: 700, fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", paddingBottom: 8, paddingRight: 16 }}>Answered</th>
                  )}
                  <th style={{ textAlign: "right", color: SLATE_MUTED, fontWeight: 700, fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", paddingBottom: 8 }}>Score</th>
                </tr>
              </thead>
              <tbody>
                {result.sections.map((s) => {
                  const color = s.pct !== null ? getHealthColor(s.pct) : SLATE_MUTED;
                  const mainAnsweredInSection = s.answered - s.followUpsAnswered;
                  const sectionMainTotal = MAIN_QUESTIONS.filter((q) => q.section === s.id).length;
                  return (
                    <tr key={s.id} data-testid={`section-${s.id}`} style={{ borderTop: `1px solid ${SLATE_BORDER}` }}>
                      <td style={{ paddingTop: 10, paddingBottom: 10, paddingRight: 16, color: BLACK, fontWeight: 600 }}>{s.title}</td>
                      {mode === "FULL" && (
                        <td style={{ paddingTop: 10, paddingBottom: 10, paddingRight: 16, textAlign: "right", color: SLATE_TEXT }}>
                          {mainAnsweredInSection}/{sectionMainTotal}
                        </td>
                      )}
                      {mode === "FULL" && (
                        <td style={{ paddingTop: 10, paddingBottom: 10, paddingRight: 16, textAlign: "right", color: SLATE_TEXT }}>
                          {s.followUpsAnswered > 0 ? `+${s.followUpsAnswered}` : <span style={{ color: SLATE_MUTED }}>—</span>}
                        </td>
                      )}
                      {mode !== "FULL" && (
                        <td style={{ paddingTop: 10, paddingBottom: 10, paddingRight: 16, textAlign: "right", color: SLATE_TEXT }}>
                          {s.answered}/{s.total}
                        </td>
                      )}
                      <td style={{ paddingTop: 10, paddingBottom: 10, textAlign: "right", fontWeight: 700, color }}>
                        {s.pct !== null ? `${s.pct} / 100` : <span style={{ color: SLATE_MUTED, fontWeight: 400 }}>— not scored</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Commentary */}
        <div style={{ padding: "24px 28px", borderBottom: `1px solid ${SLATE_BORDER}`, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
          <div>
            <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.18em", color: "#15803D", textTransform: "uppercase", margin: 0, marginBottom: 10 }}>
              Strong checkpoints
            </p>
            <ul style={{ margin: 0, paddingLeft: 18, color: BLACK, fontSize: 13.5, lineHeight: 1.6 }}>
              {result.strongest.length === 0 && <li style={{ color: SLATE_TEXT }}>Not enough answered yet.</li>}
              {result.strongest.map((id) => (
                <li key={id} style={{ marginBottom: 4 }}>{SECTION_STRENGTH_LINES[id]}</li>
              ))}
            </ul>
          </div>
          <div>
            <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.18em", color: RED, textTransform: "uppercase", margin: 0, marginBottom: 10 }}>
              Gaps to close
            </p>
            <ul style={{ margin: 0, paddingLeft: 18, color: BLACK, fontSize: 13.5, lineHeight: 1.6 }}>
              {result.weakest.length === 0 && <li style={{ color: SLATE_TEXT }}>Not enough answered yet.</li>}
              {result.weakest.map((id) => (
                <li key={id} style={{ marginBottom: 4 }}>{SECTION_RISK_LINES[id]}</li>
              ))}
            </ul>
          </div>
        </div>

        {/* Actions */}
        <div style={{ padding: "20px 28px", display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <button
            type="button"
            onClick={reset}
            data-testid="result-reset"
            style={{ padding: "10px 18px", background: BLACK, color: "#FFFFFF", border: "none", borderRadius: 9999, fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", cursor: "pointer" }}
          >
            Restart
          </button>
          {inAip && (
            <>
              <button
                type="button"
                onClick={handleEmail}
                data-testid="result-email"
                style={{ padding: "10px 18px", background: "#FFFFFF", color: BLACK, border: `1px solid ${BLACK}`, borderRadius: 9999, fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", cursor: "pointer" }}
              >
                Email this score
              </button>
              {accountName && (
                <button
                  type="button"
                  onClick={handleSaveAccount}
                  data-testid="result-save-account"
                  disabled={!!savedAccountName}
                  style={{ padding: "10px 18px", background: savedAccountName ? "#15803D" : RED, color: "#FFFFFF", border: "none", borderRadius: 9999, fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", cursor: savedAccountName ? "default" : "pointer" }}
                >
                  {savedAccountName ? `Saved to ${savedAccountName}` : `Save to ${accountName}`}
                </button>
              )}
            </>
          )}
          <span style={{ flex: 1 }} />
          {!inAip && mode === "QUICK" && (
            <span style={{ fontSize: 12, color: SLATE_TEXT, maxWidth: 340, lineHeight: 1.5 }}>
              Want the full 40-question checkpoint audit with branching follow-ups?{" "}
              <a href="/contact" style={{ color: RED, fontWeight: 700, textDecoration: "underline" }}>Sign the NDA</a>
              {" "}to unlock the complete report.
            </span>
          )}
        </div>
      </div>
    );
  }

  // ── QUESTION ───────────────────────────────────────────────────────────────
  if (!currentQ) return null;
  const sec = sectionFor(currentQ.section);
  const selected = answers[currentQ.id];
  const isFollowUp = !!currentQ.isFollowUp;

  // Progress bar based on main questions: barWidth advances on each main question
  const mainTotal = MAIN_QUESTIONS.length; // always 40 in full mode, 7 in quick
  const barTotal = mode === "QUICK" ? QUICK_QUESTIONS.length : mainTotal;
  const barProgress = mode === "QUICK" ? stepIndex : mainIndexUpToNow - 1;
  const barWidth = Math.round((barProgress / barTotal) * 100);

  // Follow-ups asked so far (after the current position)
  const followUpsAskedSoFar = activeQueue.slice(0, stepIndex).filter((q) => q.isFollowUp).length;

  return (
    <div
      style={{
        background: "#FFFFFF",
        border: `1px solid ${SLATE_BORDER}`,
        borderRadius: 16,
        overflow: "hidden",
        boxShadow: "0 1px 2px rgba(15,23,42,0.04), 0 12px 32px -16px rgba(15,23,42,0.12)",
      }}
      data-testid="calculator-question"
    >
      {/* PROGRESS HEADER */}
      <header
        style={{ position: "sticky", top: 0, zIndex: 5, background: BLACK, color: "#FFFFFF" }}
        data-testid="calculator-progress-header"
      >
        {/* 4px progress bar at very top */}
        <div style={{ height: 4, background: "rgba(255,255,255,0.12)" }}>
          <div style={{ height: "100%", width: `${barWidth}%`, background: RED, transition: "width 0.35s ease" }} />
        </div>
        <div style={{ padding: "12px 24px 14px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div>
            {mode === "FULL" ? (
              <>
                <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.06em" }} data-testid="progress-main">
                  Question {mainIndexUpToNow} of {mainTotal} main
                </span>
                {followUpsAskedSoFar > 0 && (
                  <>
                    <span style={{ fontSize: 12, color: "#8E8E94", margin: "0 8px" }}>·</span>
                    <span style={{ fontSize: 12, color: "#8E8E94", letterSpacing: "0.04em" }} data-testid="progress-followups">
                      plus {followUpsAskedSoFar} follow-up{followUpsAskedSoFar !== 1 ? "s" : ""} so far
                    </span>
                  </>
                )}
              </>
            ) : (
              <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.06em" }}>
                Question {stepIndex + 1} of {QUICK_QUESTIONS.length}
              </span>
            )}
            <span style={{ fontSize: 12, color: "#8E8E94", margin: "0 10px" }}>·</span>
            <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase", color: "#FCA5A5" }}>
              {sec.title.toUpperCase()}
            </span>
          </div>
          <div style={{ fontSize: 11, color: "#8E8E94", letterSpacing: "0.06em" }}>
            {mode === "QUICK" ? "Quick Score · No follow-ups" : `Section ${currentSectionIdx + 1} of ${sectionsInRun.length}`}
          </div>
        </div>
      </header>

      {/* Question body */}
      <div
        key={currentQ.id}
        style={{
          padding: isFollowUp ? `20px 28px 18px ${28 + FOLLOW_UP_INDENT}px` : "26px 28px 18px",
          animation: "calc-slide-in 0.28s ease-out",
          background: isFollowUp ? FOLLOW_UP_BG : "#FFFFFF",
          borderLeft: isFollowUp ? `4px solid ${SLATE_BORDER}` : "none",
          marginLeft: isFollowUp ? FOLLOW_UP_INDENT : 0,
          marginRight: isFollowUp ? 0 : 0,
        }}
      >
        <style>{`
          @keyframes calc-slide-in {
            from { opacity: 0; transform: translateX(10px); }
            to   { opacity: 1; transform: translateX(0); }
          }
        `}</style>

        {/* Follow-up tag */}
        {isFollowUp && (
          <div style={{ marginBottom: 8 }}>
            <span style={{
              display: "inline-block",
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.18em",
              color: SLATE_MUTED,
              textTransform: "uppercase",
              padding: "2px 8px",
              border: `1px solid ${SLATE_BORDER}`,
              borderRadius: 4,
              background: "#FFFFFF",
            }}>
              Follow-up
            </span>
          </div>
        )}

        <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.18em", color: isFollowUp ? SLATE_MUTED : SLATE_TEXT, textTransform: "uppercase", margin: 0, marginBottom: 6 }}>
          {sec.caption}
        </p>
        <h3 style={{ fontSize: isFollowUp ? 16 : 18, fontWeight: 700, color: BLACK, lineHeight: 1.4, margin: 0, marginBottom: currentQ.helper ? 8 : 20 }}>
          {currentQ.stem}
        </h3>
        {currentQ.helper && (
          <p style={{ fontSize: 12.5, color: SLATE_TEXT, lineHeight: 1.55, margin: 0, marginBottom: 16, fontStyle: "italic" }}>
            {currentQ.helper}
          </p>
        )}

        {/* 5-point graded options */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {GRADED_OPTIONS.map((opt) => {
            const active = selected === opt.value;
            const pts = opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => pick(currentQ.id, opt.value)}
                data-testid={`option-${currentQ.id}-${opt.value}`}
                style={{
                  textAlign: "left",
                  padding: "11px 14px 11px 16px",
                  borderRadius: 8,
                  border: active ? `2px solid ${RED}` : `1px solid ${SLATE_BORDER}`,
                  borderLeft: `4px solid ${opt.leftBorderColor}`,
                  background: active ? "#FEF2F2" : "#FFFFFF",
                  color: BLACK,
                  fontSize: 15,
                  lineHeight: 1.45,
                  cursor: "pointer",
                  transition: "background 0.1s, border-color 0.1s",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 12,
                }}
                onMouseEnter={(e) => { if (!active) (e.currentTarget as HTMLButtonElement).style.background = BG; }}
                onMouseLeave={(e) => { if (!active) (e.currentTarget as HTMLButtonElement).style.background = "#FFFFFF"; }}
              >
                <span>{opt.label}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: SLATE_MUTED, whiteSpace: "nowrap", flexShrink: 0 }}>
                  {pts} {pts === 1 ? "pt" : "pts"}
                </span>
              </button>
            );
          })}
        </div>

        {/* Skip link */}
        <div style={{ marginTop: 12, textAlign: "center" }}>
          <button
            type="button"
            onClick={() => skip(currentQ.id)}
            data-testid={`skip-${currentQ.id}`}
            style={{ background: "none", border: "none", color: SLATE_MUTED, fontSize: 12, cursor: "pointer", textDecoration: "underline", padding: "4px 8px" }}
          >
            Skip — unknown for now
          </button>
        </div>
      </div>

      {/* Footer nav */}
      <div style={{ padding: "12px 28px 22px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, borderTop: `1px solid ${SLATE_BORDER}` }}>
        <button
          type="button"
          onClick={back}
          disabled={stepIndex === 0}
          data-testid="question-back"
          style={{
            padding: "8px 16px",
            background: "#FFFFFF",
            color: stepIndex === 0 ? "#C9C9CC" : BLACK,
            border: `1px solid ${stepIndex === 0 ? SLATE_BORDER : BLACK}`,
            borderRadius: 9999,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            cursor: stepIndex === 0 ? "not-allowed" : "pointer",
          }}
        >
          ← Back
        </button>
        <button
          type="button"
          onClick={reset}
          data-testid="question-reset"
          style={{ padding: "8px 14px", background: "transparent", color: SLATE_TEXT, border: "none", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", cursor: "pointer" }}
        >
          Start over
        </button>
        <button
          type="button"
          onClick={() => {
            if (selected !== undefined) {
              const newQueue = mode === "QUICK" ? QUICK_QUESTIONS : buildQueue({ ...answers });
              if (stepIndex + 1 >= newQueue.length) setSubmitted(true);
              else setStepIndex(stepIndex + 1);
            }
          }}
          disabled={selected === undefined}
          data-testid="question-next"
          style={{
            padding: "8px 18px",
            background: selected === undefined ? "#FCA5A5" : RED,
            color: "#FFFFFF",
            border: "none",
            borderRadius: 9999,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            cursor: selected === undefined ? "not-allowed" : "pointer",
            opacity: selected === undefined ? 0.7 : 1,
          }}
        >
          {stepIndex + 1 >= totalCount ? "See score →" : "Next →"}
        </button>
      </div>
    </div>
  );
}

// Re-export for callers
export type { CalculatorResult } from "@shared/calculator-questions-v2_3";
